'use strict'

import ColorSample from '../models/ColorSample'
import Config from '../Config.js'

const ImageType = Object.freeze({
    imageTypeRGB: 0,
    imageTypeRGBA: 1,
    imageTypeGrayscale: 2,
})

class CXImage {
    constructor() {
        this.m_width = 0
        this.m_height = 0
        this.imageType = ImageType.imageTypeGrayscale
        this.canvas_name = null
        this._data = new Uint8ClampedArray(1)
    }

    static get ImageType() {
        return ImageType
    }

    get data() {
        return this._data
    }

    get length() {
        return this._data.length
    }

    /**
     * Initialize internal 1-byte-per-pixel mask from an RGBA ImageData.
     * - Uses the RED channel as the mask index (grayscale sprite: R==G==B).
     * - Valid indices: 1..ColorSample.MAX_NUMBER_OF_COLORS (inclusive).
     * - 255 (and 0/out-of-range) → DEFAULT_MASKING_COLOR (unpainted).
     * - Remaps valid indices through `shiftCounter` (Map), falling back to 255 if missing.
     *
     * @param {ImageData} imageData
     * @param {Map<number, number>} shiftCounter  // index -> compacted index
     */
    initWithImageData(imageData, shiftCounter) {
        const src = imageData.data // Uint8ClampedArray (RGBA)
        const pixelCount = src.length >>> 2 // divide by 4
        const out = new Uint8ClampedArray(pixelCount)

        // Track the logical size to match the source bitmap
        this.m_width = imageData.width
        this.m_height = imageData.height
        this.imageType = ImageType.imageTypeGrayscale

        const MAX = ColorSample.MAX_NUMBER_OF_COLORS
        const UNPAINTED = Config.DEFAULT_MASKING_COLOR

        // Iterate pixels: i walks RGBA, p walks mask buffer
        for (let i = 0, p = 0; p < pixelCount; i += 4, p++) {
            const v = src[i] // red channel as mask index

            // Explicit 255 (unpainted), 0, or out-of-range → UNPAINTED
            if (v === 255 || v === 0 || v > MAX) {
                out[p] = UNPAINTED
            } else {
                // Map through shiftCounter; default to UNPAINTED if missing/undefined
                out[p] = shiftCounter?.get(v) ?? UNPAINTED
            }
        }

        // Store buffer (do not assign to getter-only properties)
        this._data = out
    }

    initWithSize(_w, _h) {
        this.m_width = _w
        this.m_height = _h
        this.imageType = ImageType.imageTypeGrayscale
        this._data = new Uint8ClampedArray(this.m_width * this.m_height)

        const UNPAINTED = Config.DEFAULT_MASKING_COLOR
        for (let i = 0; i < this._data.length; i++) {
            this._data[i] = UNPAINTED
        }
    }
}
export default CXImage
